import type { Editor } from '@tiptap/core';
import type {
  CustomActionProps,
  SingleAction,
  StepContentProps,
} from './types';

import css from '@emotion/css';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { KizenTypography } from '__app/typography';
import TextInput from '__components/Inputs/TextInput';
import { EmailPicker } from '__components/Kizen/EmailPicker';
import Loader from '__components/Kizen/Loader';
import TextTemplater from 'components/Kizen/TextTemplater';
import ConfirmationModal from '__components/Modals/presets/ConfirmationModal';
import { useToast, toastVariant } from '__components/ToastProvider';
import { useContactsMergeFields } from '__hooks/useMergeFieldsBase';
import {
  type UseGetSummaryOptions,
  CUSTOM_ACTION_IDENTIFIER,
  useGetSummary,
} from 'pages/Common/bulkactions/model';
import SummaryStep from 'pages/Common/bulkactions/SummaryStep';
import LibraryService, { templateForApi } from 'services/LibraryService';
import PerformActionService from 'services/PerformActionService';
import { textMessagingProvidersQuery } from 'query/text-messaging-provider';
import { ActionModal } from './ActionModal';

const TOAST_DELAY = 3000;

export const checkSendEmailAvailability = (options: {
  access: StepContentProps['access'] | null;
  email: string;
  emailStatus: StepContentProps['emailStatus'];
  t: (x: string) => string;
}) => {
  const { access = null, email, emailStatus, t } = options;

  if (access && !access.edit) {
    return {
      canSendEmail: false,
      notPermittedText: t(
        'You do not have the right permissions to email this Contact. Please check with your administrator if you have further questions.'
      ),
    };
  } else if (emailStatus === 'suppression_list') {
    return {
      canSendEmail: false,
      notPermittedText: t(
        'This Contact is on suppression list and cannot be emailed.'
      ),
    };
  } else if (!email) {
    return {
      canSendEmail: false,
      notPermittedText: t(
        'This Contact has no email address saved and cannot be emailed.'
      ),
    };
  } else if (emailStatus === 'unsubscribed') {
    return {
      canSendEmail: true,
      unsubscribed: true,
      notPermittedText: t(
        'This Contact is Unsubscribed from All. Do you want to send email anyway?'
      ),
    };
  }

  return { canSendEmail: true, notPermittedText: null };
};

export const Wrapper = styled.div<{ minHeight?: string | null }>`
  flex: 1;
  margin-bottom: 17px;

  ${({ minHeight }) =>
    minHeight &&
    css`
      min-height: ${minHeight};
    `}
`;

export const useSendTextFormAccess = () => {
  const canConfigureTextMessaging = useSelector((s: any) => {
    const textingAccess =
      s.authentication.access.sections.settings_section.text_messaging;

    return textingAccess.edit;
  });

  const queryOptions = useMemo(() => {
    return textMessagingProvidersQuery({
      select: (data) => {
        return Boolean(data[0]);
      },
    });
  }, []);
  const providerQuery = useQuery(queryOptions);
  const { data: isProviderConfigured, isLoading } = providerQuery;

  const showForm = Boolean(!isLoading && isProviderConfigured);
  const showTextWithLink = Boolean(
    !isLoading && !isProviderConfigured && canConfigureTextMessaging
  );
  const showGenericText = Boolean(
    !isLoading && !isProviderConfigured && !canConfigureTextMessaging
  );

  return { isLoading, showForm, showTextWithLink, showGenericText };
};

export const SendText = (props: StepContentProps) => {
  const { entityId, fullName, isMobile, mobilePhone, onHide } = props;

  const { t } = useTranslation();
  const [showToast] = useToast();
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [performingAction, setPerformingAction] = useState(false);

  const { categorizedFieldOptions: mergeFields } = useContactsMergeFields();

  const { isLoading, showForm, showTextWithLink, showGenericText } =
    useSendTextFormAccess();

  const handleSendText = async () => {
    try {
      setPerformingAction(true);
      await LibraryService.sendText({ content: text, name, sendTo: entityId });
      onHide();
      showToast({
        message: t('The text has been successfully queued to 1 Contact'),
        variant: toastVariant.SUCCESS,
        delay: TOAST_DELAY,
      });
    } catch (err) {
      showToast({
        message: t(
          'The text was not successfully queued. Please try again or contact Kizen support.'
        ),
        variant: toastVariant.FAILURE,
        delay: TOAST_DELAY,
      });
    } finally {
      setPerformingAction(false);
    }
  };

  if (!mobilePhone) {
    return (
      <ConfirmationModal
        show
        actionBtnColor="blue"
        buttonText={t('Close')}
        onHide={onHide}
        heading={t('Cannot Perform This Action')}
        leftBtn={null}
        onConfirm={onHide}
      >
        {t(
          'This Contact has no mobile phone saved and cannot receive text messages.'
        )}
      </ConfirmationModal>
    );
  }

  return (
    <ActionModal
      show
      disableButton={performingAction || !text.trim() || !name.trim()}
      isMobile={isMobile}
      rightButtonTitle={t('Send Text')}
      title={t('Send Text to {{fullName}}', { fullName })}
      onHide={onHide}
      onSubmit={handleSendText}
    >
      <Wrapper minHeight={showForm ? '315px' : null}>
        <Loader loading={isLoading}>
          {showForm && (
            <>
              <TextInput
                label={t('Name Your Broadcast')}
                placeholder={t('Enter Broadcast Name')}
                value={name}
                onChange={(value: string) => setName(value)}
              />
              <TextTemplater
                onChange={({ editor }: { editor: Editor }) => {
                  setText(editor.getText());
                }}
                customize={{ mergeFields }}
                textareaOptions={{
                  initialHeight: 140,
                }}
              />
            </>
          )}
          {showTextWithLink && (
            <div>
              <KizenTypography as="span">
                {t(
                  'A text messaging provider needs to be configured to send text messages. This can be done under '
                )}
              </KizenTypography>
              <KizenTypography
                type="anchor"
                href="/settings/privacy-settings"
                target="_blank"
              >
                {t('privacy settings.')}
              </KizenTypography>
            </div>
          )}
          {showGenericText && (
            <>
              <KizenTypography>
                {t(
                  'A text messaging provider needs to be configured to send text messages. An administrator will need to set this up under Privacy Settings.'
                )}
              </KizenTypography>
            </>
          )}
        </Loader>
      </Wrapper>
    </ActionModal>
  );
};

export const SendEmail = (props: StepContentProps) => {
  const {
    access,
    entityId,
    email: emailProp,
    emailStatus,
    fullName,
    onHide,
    isMobile,
  } = props;

  const { t } = useTranslation();
  const [email, setEmail] = useState(null);
  const [showToast] = useToast();
  const [performingAction, setPerformingAction] = useState(false);
  const {
    canSendEmail,
    notPermittedText,
    unsubscribed: unsubscribedValue,
  } = checkSendEmailAvailability({
    access,
    email: emailProp,
    emailStatus,
    t,
  });
  const [unsubscribed, setUnsubscribed] = useState(unsubscribedValue);

  const handleContinue = useCallback(() => {
    setUnsubscribed(false);
  }, []);

  const handleSendEmail = async () => {
    try {
      setPerformingAction(true);
      await LibraryService.sendEmail({
        ...templateForApi(email),
        sendTo: entityId,
      });
      onHide();
      showToast({
        message: t('The email has been successfully queued to 1 Contact'),
        variant: toastVariant.SUCCESS,
        delay: TOAST_DELAY,
      });
    } catch (err) {
      showToast({
        message: t('The email could not be sent'),
        variant: toastVariant.FAILURE,
        delay: TOAST_DELAY,
      });
    } finally {
      setPerformingAction(false);
    }
  };

  return canSendEmail && !unsubscribed ? (
    <ActionModal
      disableButton={performingAction || !email}
      onHide={onHide}
      title={t('Send Email to {{fullName}}', { fullName })}
      show
      onSubmit={handleSendEmail}
      isMobile={isMobile}
      rightButtonTitle="Send Email"
    >
      <Wrapper {...props}>
        <EmailPicker email={email} onChange={setEmail} entityId={entityId} />
      </Wrapper>
    </ActionModal>
  ) : (
    <ConfirmationModal
      actionBtnColor={unsubscribed ? 'green' : 'blue'}
      show
      buttonText={unsubscribed ? t('Continue') : t('Close')}
      onHide={onHide}
      heading={
        unsubscribed
          ? t('Please Confirm This Action')
          : t('Cannot Perform This Action')
      }
      leftBtn={unsubscribed ? undefined : null}
      onConfirm={unsubscribed ? handleContinue : onHide}
    >
      {notPermittedText}
    </ConfirmationModal>
  );
};

const selectedAction: UseGetSummaryOptions['selectedAction'] = {
  value: CUSTOM_ACTION_IDENTIFIER,
};

export const CustomAction = (props: CustomActionProps) => {
  const { entityId, fullName, onHide, isMobile, isClient, customAction } =
    props;

  const { t } = useTranslation();
  const [showToast] = useToast();
  const [performingAction, setPerformingAction] = useState(false);

  const selection = useMemo<UseGetSummaryOptions['selection']>(() => {
    return { value: 'individual', checkedIds: [entityId] };
  }, [entityId]);

  const objectInfo = useMemo(() => {
    return {
      names: isClient
        ? {
            singular: t('Contact'),
            plural: t('Contacts'),
          }
        : {
            singular: t('Record'),
            plural: t('Records'),
          },
    };
  }, [isClient, t]);

  const [{ summary, isFetching, isPolling }, getSummaryFn] = useGetSummary({
    selection,
    selectedAction,
    customAction,
  });

  const isLoadingSummary = isFetching || isPolling;
  const isFailed = !isLoadingSummary && !summary?.cache_key;

  useEffect(() => {
    getSummaryFn();
  }, [getSummaryFn]);

  const handleCancel = useCallback(() => {
    onHide();
  }, [onHide]);

  const handleSubmit = async () => {
    try {
      setPerformingAction(true);
      await PerformActionService.performCustomAction({
        entity_records_set_key: summary?.cache_key,
        custom_action_id: customAction.id,
      });

      showToast({
        variant: toastVariant.SUCCESS,
        message: t(
          '{{noun}} has been successfully queued on {{name}}. You will receive an email once this action is complete.',
          {
            name: fullName,
            noun: customAction.name,
          }
        ),
        delay: TOAST_DELAY,
      });
    } catch (error) {
      showToast({
        variant: toastVariant.FAILURE,
        message: t(
          'The {{noun}} was not successfully queued. Please try again or contact Kizen support.',
          {
            noun: customAction.name,
          }
        ),
      });
    } finally {
      setPerformingAction(false);
      onHide();
    }
  };

  return (
    <ActionModal
      show
      disableButton={performingAction || isFetching || isPolling}
      onHide={handleCancel}
      title={t('{{name}} on {{fullName}}', {
        fullName,
        name: customAction.name,
      })}
      onSubmit={handleSubmit}
      isMobile={isMobile}
      isFailed={isFailed}
    >
      <Wrapper>
        <Loader loading={isFetching || isPolling}>
          <SummaryStep
            userAction={selectedAction}
            summary={summary?.stats && { ...summary.stats, text: fullName }}
            isLoadingSummary={isFetching || isPolling}
            customAction={customAction}
            objectInfo={objectInfo}
          />
        </Loader>
      </Wrapper>
    </ActionModal>
  );
};

export const performActionOptions: SingleAction[] = [
  {
    value: 'send_email',
    label: 'Send Email',
    component: SendEmail,
    permission: 'single_send_message',
  },
  {
    value: 'send_text',
    label: 'Send Text',
    component: SendText,
    permission: 'single_send_message',
  },
];
